import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { defaultSchema } from "hast-util-sanitize";

/* ============ i18n ============ */
const RAW = {
  ...import.meta.glob("./dictionary/*.json", { eager: true }),
  ...import.meta.glob("../dictionary/*.json", { eager: true }),
};
const EN = {
  code: "en",
  language: "English",
  strings: {
    language:"Language", light_mode:"Light mode", dark_mode:"Dark mode",
    nfc_ready:"NFC ready", nfc_unavailable:"NFC unavailable (iPhone lacks Web NFC)",
    kicker:"Payments • NFC • Invoices • Multilingual • Multicurrency",
    quick_actions:"Quick actions",
    hero_sub:"Create invoices on Hive/HBD/Lightning via QR or NFC. Manage your card and read tutorials in-app.",
    create_invoice:"Create invoice", invoice_desc:"Hive / HBD / Lightning with QR and NFC.",
    create_card:"Create card", create_card_desc:"Write your NFC card (Android/Chrome). iPhone has no Web NFC.",
    need_android_chrome:"Needs Android/Chrome for Web NFC",
    my_card:"My card", my_card_desc:"Load via QR, inspect data, top up and more.",
    info1_title:"Web NFC (Android)", info1_text:"Create cards, read data, and use 4-digit PIN + UID. iPhone: no Web NFC (for now).",
    info2_title:"QR & NFC invoices", info2_text:"Generate charges on Hive/HBD/Lightning and share via QR or NFC tag.",
    info3_title:"Multilingual & multicurrency", info3_text:"UI in ES/EN/PT and currencies HBD/USD/BTC (extensible).",
    tutorials_title:"HiveDebit Tutorials (Hive)", tutorials_desc:"We render posts in our own UI. Connect Hive Keychain to vote, tip, and comment.",
    powered_by_hive:"Powered by Hive", tutorial_badge:"Tutorial",
    open:"Open", view_on_peakd:"View on PeakD",
    connect_keychain:"Connect Hive Keychain", close:"Close",
    hive_user:"Hive user", your_user:"youruser",
    keychain_use_notice:"We’ll use this account to sign with Keychain when you vote or comment.",
    connect:"Connect", keychain_missing:"Hive Keychain not detected. Install it and try again.",
    loading:"Loading…", comments:"Comments", chronological_order:"chronological order",
    be_first_to_comment:"Be the first to comment.",
    comment_placeholder:"Write your comment (Markdown supported)…",
    publish_comment:"Publish comment",
    comment_notice:"When publishing we’ll ask your Hive user (Keychain) if not connected.",
    disconnect:"Disconnect",
    upvote:"Upvote", downvote:"Downvote", tip_hbd:"Tip (HBD)"
  }
};
function useI18n(){
  const dicts = useMemo(() => {
    const list = Object.entries(RAW).map(([p,m])=>{
      const raw = m?.default ?? m ?? {};
      const code = (raw.code || p.match(/([a-z-]+)\.json$/i)?.[1] || "xx").toLowerCase();
      const name = raw.language || raw.name || code.toUpperCase();
      const src = raw.strings ? raw.strings : raw;
      const strings = Object.fromEntries(Object.entries(src).filter(([k])=>!["code","language","name","strings"].includes(k)));
      return { code, language:name, strings };
    });
    const map = new Map(); map.set("en",{code:"en",language:"English",strings:EN.strings});
    for(const d of list) if(!map.has(d.code)) map.set(d.code,d);
    return Array.from(map.values());
  },[]);
  const initial = useMemo(()=>{
    const saved = localStorage.getItem("lang");
    const nav = (navigator.language||"en").slice(0,2).toLowerCase();
    return dicts.find(d=>d.code===(saved||nav)) || dicts.find(d=>d.code==="en") || dicts[0];
  },[dicts]);
  const [lang,setLang]=useState(initial.code);
  const dict = useMemo(()=>dicts.find(d=>d.code===lang)||initial,[dicts,lang,initial]);
  useEffect(()=>{
    localStorage.setItem("lang",lang);
    document.body.classList.add("lang-swap");
    const t=setTimeout(()=>document.body.classList.remove("lang-swap"),220);
    return ()=>clearTimeout(t);
  },[lang]);
  const t=(k)=> (dict?.strings?.[k] ?? EN.strings[k] ?? k);
  return {lang,setLang,t,dicts};
}

/* ============ Tema ============ */
function useTheme(){
  const [theme,setTheme]=useState(()=>{
    const saved = localStorage.getItem("theme");
    if (saved) return saved;
    return document.documentElement.classList.contains("dark")?"dark":"light";
  });
  useEffect(()=>{
    document.documentElement.classList.toggle("dark", theme==="dark");
    let meta=document.querySelector('meta[name="theme-color"]');
    if(!meta){ meta=document.createElement("meta"); meta.setAttribute("name","theme-color"); document.head.appendChild(meta); }
    meta.setAttribute("content", theme==="dark"?"#0E0E12":"#F4F5F7");
  },[theme]);
  function toggle(){
    const next=theme==="dark"?"light":"dark";
    setTheme(next);
    document.body.classList.add("theme-swap");
    setTimeout(()=>document.body.classList.remove("theme-swap"),220);
    localStorage.setItem("theme",next);
  }
  return {theme,toggle};
}

/* ============ BG/Parallax (suave) ============ */
function useParallax(){
  useEffect(()=>{
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    let ticking=false;
    const onScroll=()=>{
      if(ticking) return;
      ticking=true;
      requestAnimationFrame(()=>{
        const y=window.scrollY||0;
        document.documentElement.style.setProperty("--plxA",(y*0.06).toFixed(1)+"px");
        document.documentElement.style.setProperty("--plxB",(y*0.12).toFixed(1)+"px");
        ticking=false;
      });
    };
    onScroll(); window.addEventListener("scroll",onScroll,{passive:true});
    return ()=>window.removeEventListener("scroll",onScroll);
  },[]);
}

/* ============ Hive utils ============ */
const HIVE_RPC="https://api.hive.blog";
async function hive_get_content(author,permlink){
  const r=await fetch(HIVE_RPC,{
    method:"POST",headers:{"content-type":"application/json"},
    body:JSON.stringify({jsonrpc:"2.0",method:"condenser_api.get_content",params:[author.replace(/^@/,''),permlink],id:1})
  });
  return (await r.json()).result;
}
async function hive_get_content_replies(author,permlink){
  const r=await fetch(HIVE_RPC,{
    method:"POST",headers:{"content-type":"application/json"},
    body:JSON.stringify({jsonrpc:"2.0",method:"condenser_api.get_content_replies",params:[author.replace(/^@/,""),permlink],id:1})
  });
  return (await r.json()).result||[];
}
function parsePeakD(url){
  try{ const u=new URL(url); const p=u.pathname.split("/").filter(Boolean);
    const a=(p[1]||"").replace("@",""); const per=p[2]||"";
    if(!a||!per) throw 0; return {author:a,permlink:per};
  }catch{ return null; }
}
function excerpt(md,max=180){
  const text=md
    .replace(/<\/?center\s*\/?>/gi," ")
    .replace(/<[^>]+>/g," ")
    .replace(/!\[[^\]]*\]\([^)]+\)/g,"")
    .replace(/\[[^\]]*\]\([^)]+\)/g,"")
    .replace(/[`*_>#-]/g," ").replace(/\s+/g," ").trim();
  return text.length>max?text.slice(0,max)+"…":text;
}

/* ============ Toasts ============ */
function useToasts(){
  const [toasts,setToasts]=useState([]);
  function push(type, msg){
    const id=Date.now()+Math.random();
    setToasts(t=>[...t,{id,type,msg}]);
    setTimeout(()=>dismiss(id), 3000);
  }
  function dismiss(id){ setToasts(t=>t.filter(x=>x.id!==id)); }
  const ui=(
    <div className="toasts">
      {toasts.map(t=>(
        <div key={t.id} className={`toast ${t.type}`}>
          <span>{t.msg}</span>
          <button onClick={()=>dismiss(t.id)}>✕</button>
        </div>
      ))}
    </div>
  );
  return { push, ui };
}

/* ============ Keychain ============ */
const hasKeychain=()=> typeof window!=="undefined" && !!window.hive_keychain;
function useKeychain(toast,t){
  const [account,setAccount]=useState(null);
  const disconnect=()=>setAccount(null);
  function ok(r,success,fail){ r?.success ? toast.push("ok",success) : toast.push("err", fail || (r?.error||"Error")); }
  function ensure(){ if(!hasKeychain()){ toast.push("err", t("keychain_missing")); return false; } return true; }
  function vote(author,permlink,weight=10000){
    if(!account || !ensure()) return;
    window.hive_keychain.requestVote(account,author,permlink,weight,(r)=> ok(r,t("upvote")+" ✅", "Error voting ❌"));
  }
  function tip(author){
    if(!account || !ensure()) return;
    const amount=prompt("Monto en HBD (ej: 0.100):"); // prompt breve, no alert
    if(!amount) return;
    window.hive_keychain.requestTransfer(account,author,amount,"Tip desde HiveDebit","HBD",(r)=> ok(r,t("tip_hbd")+" ✅","Error tip ❌"));
  }
  function broadcastComment(parentAuthor,parentPermlink,body){
    if(!account || !ensure()) return;
    const permlink=`re-${parentPermlink}-${Date.now()}`.toLowerCase();
    const op=["comment",{
      parent_author:parentAuthor,
      parent_permlink:parentPermlink,
      author:account, permlink, title:"",
      body, json_metadata:JSON.stringify({app:"hivedebit/0.1",format:"markdown"})
    }];
    window.hive_keychain.requestBroadcast(account,[op],"Posting",(r)=> ok(r,"Comment published ✅","Error commenting ❌"));
  }
  return {account,setAccount,disconnect,vote,tip,broadcastComment};
}

/* ============ Iconos ============ */
const IconInvoice = () => (<svg width="22" height="22" viewBox="0 0 24 24"><path d="M7 3h8l4 4v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" fill="currentColor" opacity=".15"/><path d="M7 3h8l4 4v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="1.6" fill="none"/><path d="M8 12h8M8 15h8M8 9h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>);
const IconCard = () => (<svg width="22" height="22" viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="2" fill="currentColor" opacity=".15"/><rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" fill="none"/><path d="M3 10h18" stroke="currentColor" strokeWidth="1.6"/><rect x="6.5" y="13" width="5" height="2.8" rx="0.6" fill="currentColor"/></svg>);
const IconID = () => (<svg width="22" height="22" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="16" rx="2" fill="currentColor" opacity=".15"/><rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" fill="none"/><circle cx="9" cy="10" r="2.4" fill="currentColor"/><path d="M6.5 15.5c.7-1.5 2-2.4 3.5-2.4s2.8.9 3.5 2.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none"/><path d="M14 9h4M14 12h4M14 15h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>);

/* ============ LangMenu ============ */
function LangMenu({ value, onChange, options, label }){
  const [open,setOpen]=useState(false);
  const current = options.find(o=>o.code===value) || options[0];
  useEffect(()=>{
    const f=(e)=>{ if(!e.target.closest(".lang-menu")) setOpen(false); };
    window.addEventListener("click",f);
    return ()=>window.removeEventListener("click",f);
  },[]);
  return (
    <div className={`lang-menu ${open?"open":""}`}>
      <button className="chip" aria-label={label} onClick={()=>setOpen(o=>!o)}>
        <span className="chip-dot" />
        <span className="chip-text">{current.language || current.name}</span>
        <span className="chip-caret">▾</span>
      </button>
      <div className="lang-pop">
        {options.map(o=>(
          <button key={o.code} className={`lang-item ${o.code===value?"active":""}`}
                  onClick={()=>{onChange(o.code); setOpen(false);}}>
            {o.language || o.name}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ============ PostCard ============ */
function PostCard({ author, permlink, onOpen, t }){
  const [data,setData]=useState(null);
  useEffect(()=>{ let alive=true;
    hive_get_content(author,permlink).then(r=>alive&&setData(r));
    return ()=>alive=false;
  },[author,permlink]);
  const title=data?.title || `${author}/${permlink}`;
  const prev=data?excerpt(data.body||""):t("loading");
  let cover=null; try{ const meta=data?.json_metadata?JSON.parse(data.json_metadata):null; if(meta?.image?.length) cover=meta.image[0]; }catch{}
  return (
    <article className="card card-tutorial">
      {cover && <img src={cover} alt="" className="card-cover" loading="lazy" />}
      <div className="card-head"><span className="badge">{t("tutorial_badge")}</span><h4 className="card-title">{title}</h4></div>
      <p className="card-text clamp">{prev}</p>
      <div className="card-actions">
        <button className="btn btn-primary" onClick={()=>onOpen({author,permlink})}>{t("open")}</button>
        <a className="btn" href={`https://peakd.com/@${author}/${permlink}`} target="_blank" rel="noreferrer">{t("view_on_peakd")}</a>
      </div>
    </article>
  );
}

/* ============ Connect Modal ============ */
function ConnectKeychainModal({ open, onClose, onConnect, t }){
  const [user,setUser]=useState("");
  const avatar = user ? `https://images.hive.blog/u/${user}/avatar` : null;
  if(!open) return null;
  return (
    <div className="connect-overlay" onClick={(e)=>{ if(e.currentTarget===e.target) onClose(); }}>
      <div className="connect-sheet" onClick={(e)=>e.stopPropagation()}>
        <div className="connect-head">
          <strong>{t("connect_keychain")}</strong>
          <button className="btn" onClick={onClose}>{t("close")}</button>
        </div>
        <div className="connect-body">
          <div className="connect-user">
            <div className="connect-avatar">{avatar ? <img src={avatar} alt="avatar" /> : <div className="avatar-placeholder">👤</div>}</div>
            <label className="field grow">
              <span>{t("hive_user")}</span>
              <div className="field-input">
                <span>@</span>
                <input placeholder={t("your_user")} value={user} onChange={(e)=>setUser(e.target.value.replace(/^@/,""))} autoFocus />
              </div>
            </label>
          </div>
          <p className="muted">{t("keychain_use_notice")}</p>
          {!hasKeychain() && <p className="muted" style={{marginTop:4}}>{t("keychain_missing")}</p>}
        </div>
        <div className="connect-actions">
          <button className="btn btn-primary" onClick={()=>{ if(user.trim()){ onConnect(user.trim()); onClose(); } }}>{t("connect")}</button>
        </div>
      </div>
    </div>
  );
}

/* ============ Post Viewer ============ */
function PostViewerModal({ open, onClose, author, permlink, keychain, t, toast }){
  const [data,setData]=useState(null); const [replies,setReplies]=useState([]); const [comment,setComment]=useState("");
  const [closing,setClosing]=useState(false); const [askConnect,setAskConnect]=useState(false);

  useEffect(()=>{ if(open) document.body.classList.add("no-scroll"); return ()=>document.body.classList.remove("no-scroll"); },[open]);
  useEffect(()=>{ if(!open) return; setData(null); setReplies([]); hive_get_content(author,permlink).then(setData); hive_get_content_replies(author,permlink).then(setReplies); },[open,author,permlink]);

  const schema = useMemo(()=>{ const s=structuredClone(defaultSchema);
    s.tagNames=[...(s.tagNames||[]),"center"];
    s.attributes={...(s.attributes||{}),"*":[...(s.attributes?.["*"]||[]),"className","align"],a:[...(s.attributes?.a||[]),"href","name","target","rel"],img:[...(s.attributes?.img||[]),"src","alt","title","width","height"]};
    return s;
  },[]);

  if(!open) return null;
  const close=()=>{ setClosing(true); setTimeout(()=>{ setClosing(false); onClose(); },120); };
  const requireAcc=(fn)=>{ if(!hasKeychain()) return toast.push("err", t("keychain_missing")); if(keychain.account) return fn(); setAskConnect(true); };
  const publish=()=>{ if(!comment.trim()) return toast.push("err","Write something first"); requireAcc(()=> keychain.broadcastComment(author,permlink,comment.trim())); };

  const ProfileChip = keychain.account ? (
    <div className="profile-chip">
      <img src={`https://images.hive.blog/u/${keychain.account}/avatar`} alt={keychain.account} width="24" height="24"/>
      <span>@{keychain.account}</span>
      <button className="btn small danger" onClick={()=>keychain.disconnect()}>{t("disconnect")}</button>
    </div>
  ) : (
    <button className="btn btn-primary" onClick={()=>setAskConnect(true)}>{t("connect_keychain")}</button>
  );

  return (
    <div className={`modal-overlay ${closing?"closing":""}`} onClick={(e)=>{ if(e.currentTarget===e.target) close(); }}>
      <div className="modal-sheet" onClick={(e)=>e.stopPropagation()}>
        <div className="modal-head">
          <div><strong style={{fontSize:18}}>{data?.title || `${author}/${permlink}`}</strong><div className="muted">@{author} • {permlink}</div></div>
          <div className="modal-head-actions">
            {ProfileChip}
            <button className="btn" onClick={close}>{t("close")}</button>
          </div>
        </div>

        <div className="modal-body">
          <div className="prose">
            {data ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw,[rehypeSanitize,schema]]} linkTarget="_blank"
                components={{a:(p)=><a {...p} rel="noreferrer noopener"/>, center:({children})=><div style={{textAlign:"center"}}>{children}</div>}}>
                {data.body || "_Sin contenido_"}
              </ReactMarkdown>
            ) : (<p className="muted">{t("loading")}</p>)}
          </div>

          <div className="comments">
            <div className="comments-head"><h4>{t("comments")} ({replies?.length||0})</h4><span className="badge">{t("chronological_order")}</span></div>
            {replies?.length?(
              <ul className="comment-list">
                {replies.map(c=>(
                  <li key={c.author+c.permlink} className="comment">
                    <div className="comment-meta"><strong>@{c.author}</strong><span>• {new Date(c.created+"Z").toLocaleString()}</span></div>
                    <div className="comment-body prose">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw,[rehypeSanitize,schema]]}>{c.body||""}</ReactMarkdown>
                    </div>
                  </li>
                ))}
              </ul>
            ):<p className="comment-empty">{t("be_first_to_comment")}</p>}

            <div className="comment-editor">
              <textarea value={comment} onChange={(e)=>setComment(e.target.value)} placeholder={t("comment_placeholder")} />
              <div className="comment-editor-actions">
                {keychain.account ? (
                  <button className="btn btn-primary" onClick={publish}>{t("publish_comment")}</button>
                ) : (
                  <button className="btn btn-primary" onClick={()=>setAskConnect(true)}>{t("connect_keychain")}</button>
                )}
              </div>
              <small className="muted">{t("comment_notice")}</small>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          {keychain.account ? (
            <>
              <button className="btn" onClick={()=>keychain.vote(author,permlink,10000)}>👍 {t("upvote")}</button>
              <button className="btn" onClick={()=>keychain.vote(author,permlink,-10000)}>👎 {t("downvote")}</button>
              <button className="btn" onClick={()=>keychain.tip(author)}>💸 {t("tip_hbd")}</button>
            </>
          ) : (
            <button className="btn btn-primary" onClick={()=>setAskConnect(true)}>{t("connect_keychain")}</button>
          )}
        </div>
      </div>

      <ConnectKeychainModal open={askConnect} onClose={()=>setAskConnect(false)} onConnect={(u)=>keychain.setAccount(u)} t={t}/>
    </div>
  );
}

/* ============ Panel “Cluster” ============ */
function JeepCluster(){
  const VERSION="Grand Cherokee v0.1";
  const ROTATE=10000;
  const TEAM=[
    { user:"jthomasewsky", name:"Jan Thomasewsky", role:"Fundador y Desarrollador", avatar:"https://images.hive.blog/u/jthomasewsky/avatar" },
    { user:"starkerz", name:"Starkerz", role:"Co-Fundador - Project Manager - Inversor", avatar:"https://images.hive.blog/u/starkerz/avatar" },
    { user:"theycallmedan", name:"TheyCallMeDan", role:"Co-Fundador - Inversor", avatar:"https://images.hive.blog/u/theycallmedan/avatar" },
  ];
  const [hive,setHive]=useState(null); const [btc,setBtc]=useState(null);
  const [hChg,setHChg]=useState(null); const [bChg,setBChg]=useState(null);
  const [idx,setIdx]=useState(0); const [glow,setGlow]=useState(false);

  useEffect(()=>{ let alive=true;
    async function load(){
      try{
        const r=await fetch("https://api.coingecko.com/api/v3/simple/price?ids=hive,bitcoin&vs_currencies=usd&include_24hr_change=true");
        const j=await r.json();
        if(!alive) return;
        setHive(j?.hive?.usd??null); setBtc(j?.bitcoin?.usd??null);
        setHChg(j?.hive?.usd_24h_change??null); setBChg(j?.bitcoin?.usd_24h_change??null);
      }catch{ /* silencio, se reintenta */ }
    }
    load(); const id=setInterval(load,30000);
    return ()=>{alive=false; clearInterval(id);};
  },[]);
  useEffect(()=>{ const t=setInterval(()=>{ setIdx(i=>(i+1)%TEAM.length); setGlow(g=>!g); },ROTATE); return ()=>clearInterval(t); },[]);
  const p=TEAM[idx];
  const fU=(n)=> n==null?"—":(n>=1000?n.toLocaleString(undefined,{maximumFractionDigits:0}):n.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2}));
  const fP=(n)=> n==null?"—":`${Math.abs(n).toFixed(2)}%`; const ar=(n)=> n==null?"":(n>=0?"▲":"▼");
  return (
    <div className="cluster">
      <div className="cluster-slot">
        <div className="cluster-frame">
          <div className="cluster-kv"><span className="cluster-label">HIVE</span><span className="cluster-value">${fU(hive)}</span></div>
          <div className={`cluster-trend ${hChg>=0?"up":"down"}`}>{ar(hChg)} {fP(hChg)}</div>
          <div className="cluster-kv" style={{marginTop:6}}><span className="cluster-label">BTC</span><span className="cluster-value">${fU(btc)}</span></div>
          <div className={`cluster-trend ${bChg>=0?"up":"down"}`}>{ar(bChg)} {fP(bChg)}</div>
          <div className="cluster-kv version" style={{marginTop:8}}><span className="cluster-label">VERSION</span><span className="cluster-value">{VERSION}</span></div>
        </div>
      </div>
      <div className="cluster-slot">
        <div className="cluster-frame">
          <div key={p.user} className={`cluster-profile ${glow?"glow":""}`}>
            <img className="cluster-avatar" src={p.avatar} alt={p.user} width="72" height="72" loading="lazy" />
            <div className="cluster-name">{p.name}</div>
            <div className="cluster-role">{p.role}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============ App ============ */
export default function App(){
  const {theme,toggle}=useTheme();
  const {lang,setLang,t,dicts}=useI18n();
  const toast=useToasts();
  const keychain=useKeychain(toast,t);
  const nfc = useMemo(()=> typeof window!=="undefined" && "NDEFReader" in window,[]);
  useParallax();

  // Precarga logos (evita “pensando”)
  useEffect(()=>{ new Image().src="/HiveDebitW.png"; new Image().src="/HiveDebitB.png"; },[]);

  const posts = useMemo(()=>[
    "https://peakd.com/hive-181335/@hivedebit/rxmojmynvj",
    "https://peakd.com/hive-106130/@hivedebit/emioepnytu",
    "https://peakd.com/hive-106130/@hivedebit/xqudahfpnq",
  ].map(parsePeakD).filter(Boolean),[]);

  const [viewer,setViewer]=useState({open:false,author:"",permlink:""});

  return (
    <div className="page">
      {/* BG */}
      <div className="bg fx" aria-hidden>
        <div className="fx-layer a" />
        <div className="fx-layer b" />
      </div>

      {/* Header */}
      <header className="header">
        <div className="container header-inner">
          <a className="brand" href="/" aria-label="HiveDebit">
            <img className="brand-logo" src={theme==="dark"?"/HiveDebitB.png":"/HiveDebitW.png"} alt="HiveDebit" width="140" height="30"/>
          </a>
          <div className="header-controls">
            <span className="badge">{nfc?t("nfc_ready"):t("nfc_unavailable")}</span>
            <LangMenu value={lang} onChange={setLang} options={dicts} label={t("language")} />
            <button className="btn mode-btn" onClick={toggle} aria-label={theme==="dark"?t("light_mode"):t("dark_mode")}>
              <span className="mode-ico">{theme==="dark" ? "☀️" : "🌙"}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="hero">
        <div className="container hero-inner">
          <span className="kicker">{t("kicker")}</span>
          <h1 className="hero-title">{t("quick_actions")}</h1>
          <p className="hero-sub">{t("hero_sub")}</p>

          <div className="grid-actions" style={{marginTop:8}}>
            <button className="action-card" onClick={()=>location.assign("/invoice/new")}>
              <div className="action-head"><div className="action-ico"><IconInvoice/></div><div className="action-title">{t("create_invoice")}</div></div>
              <div className="action-desc">{t("invoice_desc")}</div>
            </button>
            <button
              className="action-card"
              onClick={()=>location.assign("/card/new")}
              disabled={!nfc}
              title={nfc?t("create_card"):t("need_android_chrome")}
            >
              <div className="action-head"><div className="action-ico"><IconCard/></div><div className="action-title">{t("create_card")}</div></div>
              <div className="action-desc">{t("create_card_desc")}</div>
            </button>
            <button className="action-card" onClick={()=>location.assign("/card/me")}>
              <div className="action-head"><div className="action-ico"><IconID/></div><div className="action-title">{t("my_card")}</div></div>
              <div className="action-desc">{t("my_card_desc")}</div>
            </button>
          </div>
        </div>
      </section>

      {/* Info */}
      <main>
        <section className="container" style={{marginTop:8}}>
          <div className="grid grid-3">
            <article className="card card-info"><h4 className="card-info-title">{t("info1_title")}</h4><p className="card-text">{t("info1_text")}</p></article>
            <article className="card card-info"><h4 className="card-info-title">{t("info2_title")}</h4><p className="card-text">{t("info2_text")}</p></article>
            <article className="card card-info"><h4 className="card-info-title">{t("info3_title")}</h4><p className="card-text">{t("info3_text")}</p></article>
          </div>
        </section>

        {/* Tutorials */}
        <section className="container" style={{marginTop:24}}>
          <div className="card" style={{marginBottom:12}}><h3 className="card-info-title">{t("tutorials_title")}</h3><p className="card-text">{t("tutorials_desc")}</p></div>
          <div className="grid grid-3">
            {posts.map(ref=>(
              <PostCard key={ref.permlink} author={ref.author} permlink={ref.permlink} onOpen={({author,permlink})=>setViewer({open:true,author,permlink})} t={t}/>
            ))}
          </div>
        </section>

        {/* Panel */}
        <section className="container" style={{marginTop:24}}>
          <JeepCluster/>
        </section>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="container footer-inner">
          <span>© {new Date().getFullYear()} HiveDebit — Grand Cherokee</span>
          <span className="badge">{t("powered_by_hive")}</span>
        </div>
      </footer>

      {/* Viewer + toasts */}
      <PostViewerModal open={viewer.open} onClose={()=>setViewer(v=>({...v,open:false}))} author={viewer.author} permlink={viewer.permlink} keychain={keychain} t={t} toast={toast}/>
      {toast.ui}
    </div>
  );
}

